// src/modules/cv/MorphologyLab.jsx
// Morphology Lab — Erosion, Dilation, Opening, Closing, Gradient, Top-Hat, Black-Hat, Median

import React, { useState, useCallback, useEffect } from 'react';
import { generateSynth, imgToImageData, imageDataToURL, morphOp, toGray } from '../../utils/cvUtils.js';
import { Panel, InfoBox } from '../../components/ui.jsx';

const TABS = ['upload', 'operations', 'compare', 'concepts'];
const Btn = ({onClick,children,color='var(--violet)',disabled,style})=>(<button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>);

const OPS = [
  {id:'dilate',   label:'Dilation',   color:'var(--cyan)',   desc:'Expands bright regions. Fills holes, joins nearby structures. Used to connect broken edges.'},
  {id:'erode',    label:'Erosion',    color:'var(--violet)', desc:'Shrinks bright regions. Removes noise, separates touching objects. Opposite of dilation.'},
  {id:'opening',  label:'Opening',    color:'var(--green)',  desc:'Erode then dilate. Removes small noise (thin spikes) while preserving larger shapes.'},
  {id:'closing',  label:'Closing',    color:'var(--gold)',   desc:'Dilate then erode. Closes small holes and gaps. Preserves overall object size.'},
  {id:'gradient', label:'Morph Grad', color:'var(--orange)', desc:'Dilation − Erosion. Produces edge/outline of the object. Alternative to gradient-based detectors.'},
  {id:'tophat',   label:'Top-Hat',    color:'var(--red)',    desc:'Original − Opening. Extracts bright small features (e.g. bright spots on dark background).'},
  {id:'blackhat', label:'Black-Hat',  color:'#f472b6',       desc:'Closing − Original. Extracts dark small features (e.g. dark spots on bright background).'},
  {id:'median',   label:'Median',     color:'var(--cyan)',   desc:'Replaces each pixel with median of circular neighborhood. Best for salt-and-pepper noise.'},
];

function computeOp(imgData, opId, radius) {
  switch(opId) {
    case 'dilate':   return morphOp(imgData, radius, 'dilate');
    case 'erode':    return morphOp(imgData, radius, 'erode');
    case 'opening':  return morphOp(morphOp(imgData, radius, 'erode'), radius, 'dilate');
    case 'closing':  return morphOp(morphOp(imgData, radius, 'dilate'), radius, 'erode');
    case 'gradient': {
      const dil = morphOp(imgData, radius, 'dilate');
      const ero = morphOp(imgData, radius, 'erode');
      const dst = new Uint8ClampedArray(dil.data.length);
      for(let i=0;i<dst.length;i+=4){dst[i]=Math.abs(dil.data[i]-ero.data[i]);dst[i+1]=Math.abs(dil.data[i+1]-ero.data[i+1]);dst[i+2]=Math.abs(dil.data[i+2]-ero.data[i+2]);dst[i+3]=255;}
      return new ImageData(dst,dil.width,dil.height);
    }
    case 'tophat': {
      const opened = morphOp(morphOp(imgData, radius, 'erode'), radius, 'dilate');
      const dst = new Uint8ClampedArray(opened.data.length);
      for(let i=0;i<dst.length;i+=4){dst[i]=Math.max(0,imgData.data[i]-opened.data[i]);dst[i+1]=Math.max(0,imgData.data[i+1]-opened.data[i+1]);dst[i+2]=Math.max(0,imgData.data[i+2]-opened.data[i+2]);dst[i+3]=255;}
      return new ImageData(dst,opened.width,opened.height);
    }
    case 'blackhat': {
      const closed = morphOp(morphOp(imgData, radius, 'dilate'), radius, 'erode');
      const dst = new Uint8ClampedArray(closed.data.length);
      for(let i=0;i<dst.length;i+=4){dst[i]=Math.max(0,closed.data[i]-imgData.data[i]);dst[i+1]=Math.max(0,closed.data[i+1]-imgData.data[i+1]);dst[i+2]=Math.max(0,closed.data[i+2]-imgData.data[i+2]);dst[i+3]=255;}
      return new ImageData(dst,closed.width,closed.height);
    }
    case 'median':   return morphOp(imgData, radius, 'median');
    default:         return imgData;
  }
}

const SYNTH = [{name:'Shapes',type:'shapes'},{name:'Circles',type:'circles'},{name:'Noise',type:'noise'},{name:'Checker',type:'checker'}];

export default function MorphologyLab({ seedImageSrc = null, hideUpload = false }) {
  const [tab, setTab] = useState(hideUpload ? 'operations' : 'upload');
  const [imgSrc, setImgSrc] = useState(null);
  const [imgData, setImgData] = useState(null);
  const [activeOp, setActiveOp] = useState('dilate');
  const [radius, setRadius] = useState(3);
  const [resultSrc, setResultSrc] = useState(null);
  const [comparePos, setComparePos] = useState(50);
  const [compareResults, setCompareResults] = useState({});
  const [processing, setProcessing] = useState(false);

  const loadSrc = useCallback(async (src) => {
    setImgSrc(src); const id = await imgToImageData(src); setImgData(id);
    setResultSrc(null); setCompareResults({});
    if (tab === 'upload') setTab('operations');
  }, [tab]);

  useEffect(() => {
    if (seedImageSrc) loadSrc(seedImageSrc);
  }, [seedImageSrc, loadSrc]);

  useEffect(() => {
    if (hideUpload && tab === 'upload') setTab('operations');
  }, [hideUpload, tab]);

  const handleFile = (file) => {
    if (!file?.type.startsWith('image/')) return;
    const r = new FileReader(); r.onload = e => loadSrc(e.target.result); r.readAsDataURL(file);
  };

  const runOp = () => {
    if (!imgData) return;
    const out = computeOp(imgData, activeOp, radius);
    setResultSrc(imageDataToURL(out));
  };

  const runAll = async () => {
    if (!imgData) return; setProcessing(true);
    await new Promise(r => setTimeout(r, 10));
    const res = {};
    for (const op of OPS) res[op.id] = imageDataToURL(computeOp(imgData, op.id, radius));
    setCompareResults(res); setProcessing(false);
  };

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {(hideUpload ? TABS.filter(t => t !== 'upload') : TABS).map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?'var(--violet)':'transparent'}`,background:tab===t?'#b08aff18':'transparent',color:tab===t?'var(--violet)':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {tab==='upload' && !hideUpload && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
          <div onDrop={e=>{e.preventDefault();handleFile(e.dataTransfer.files[0]);}} onDragOver={e=>e.preventDefault()} onClick={()=>document.getElementById('morph-file').click()} style={{border:'2px dashed var(--b1)',borderRadius:'4px',padding:'52px 24px',textAlign:'center',cursor:'pointer',background:'var(--surface)'}}>
            <input id="morph-file" type="file" accept="image/*" style={{display:'none'}} onChange={e=>handleFile(e.target.files[0])}/>
            <div style={{fontSize:'40px',opacity:.3,marginBottom:'14px'}}>◉</div>
            <div style={{fontFamily:'Syne',fontWeight:700,fontSize:'18px',marginBottom:'7px'}}>Drop Image</div>
            <div style={{fontSize:'12px',color:'var(--t3)'}}>Best on high-contrast binary-like images</div>
          </div>
          <Panel title="SYNTHETIC TEST IMAGES" accent="var(--violet)">
            {SYNTH.map(({name,type})=>(
              <div key={type} onClick={()=>loadSrc(generateSynth(type,256))} style={{display:'flex',alignItems:'center',gap:'10px',padding:'9px 12px',border:'1px solid var(--b0)',borderRadius:'3px',marginBottom:'6px',cursor:'pointer',background:'var(--raised)',transition:'all .12s'}} onMouseEnter={e=>e.currentTarget.style.borderColor='var(--violet)'} onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b0)'}>
                <div style={{flex:1}}><div style={{fontSize:'12px',fontWeight:600,color:'var(--t1)'}}>{name}</div><div style={{fontSize:'10px',color:'var(--t3)'}}>256×256 synthetic</div></div>
                <span style={{color:'var(--violet)'}}>→</span>
              </div>
            ))}
          </Panel>
        </div>
      )}

      {tab==='operations' && imgData && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="OPERATION + STRUCTURING ELEMENT" accent="var(--violet)">
            <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'12px'}}>
              {OPS.map(op=>(
                <button key={op.id} onClick={()=>setActiveOp(op.id)} style={{padding:'7px 14px',border:`1px solid ${activeOp===op.id?op.color:'var(--b0)'}`,background:activeOp===op.id?op.color+'18':'var(--raised)',color:activeOp===op.id?op.color:'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:activeOp===op.id?700:400}}>{op.label}</button>
              ))}
            </div>
            <div style={{marginBottom:'12px'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:'5px'}}>
                <span style={{fontSize:'10px',color:'var(--t3)'}}>Structuring Element Radius (disk)</span>
                <span style={{fontSize:'10px',color:'var(--violet)',fontWeight:700}}>{radius}px — {(2*radius+1)}×{(2*radius+1)} disk</span>
              </div>
              <input type="range" min={1} max={12} step={1} value={radius} onChange={e=>setRadius(+e.target.value)} style={{width:'100%',accentColor:'var(--violet)'}}/>
            </div>
            <InfoBox color={OPS.find(o=>o.id===activeOp)?.color||'var(--violet)'} icon="◉">
              {OPS.find(o=>o.id===activeOp)?.desc}
            </InfoBox>
            <div style={{marginTop:'12px'}}><Btn onClick={runOp}>APPLY →</Btn></div>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            <Panel title="ORIGINAL (grayscale)" accent="var(--t2)">
              <img src={imgSrc} alt="orig" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--b1)'}}/>
            </Panel>
            <Panel title={OPS.find(o=>o.id===activeOp)?.label||'Result'} accent="var(--violet)">
              {resultSrc ? <img src={resultSrc} alt="result" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--violet)44'}}/> : <div style={{padding:'40px',textAlign:'center',color:'var(--t3)',fontSize:'11px'}}>Click "APPLY →" to run</div>}
            </Panel>
          </div>
          {resultSrc && (
            <div style={{display:'flex',gap:'12px',alignItems:'center'}}>
              <span style={{fontSize:'10px',color:'var(--t3)'}}>SLIDER COMPARE:</span>
              <input type="range" min={0} max={100} value={comparePos} onChange={e=>setComparePos(+e.target.value)} style={{flex:1,accentColor:'var(--violet)'}}/>
              <span style={{fontSize:'10px',color:'var(--violet)',fontWeight:700}}>{comparePos}%</span>
            </div>
          )}
        </div>
      )}

      {tab==='compare' && imgData && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="ALL OPERATIONS AT r=" accent="var(--violet)">
            <div style={{display:'flex',gap:'12px',alignItems:'center',marginBottom:'12px'}}>
              <div style={{flex:1}}><div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px'}}><span style={{fontSize:'10px',color:'var(--t3)'}}>Radius</span><span style={{fontSize:'10px',color:'var(--violet)',fontWeight:700}}>{radius}px</span></div><input type="range" min={1} max={10} value={radius} onChange={e=>setRadius(+e.target.value)} style={{width:'100%',accentColor:'var(--violet)'}}/></div>
              <Btn onClick={runAll} disabled={processing}>{processing?'COMPUTING...':'RUN ALL →'}</Btn>
            </div>
          </Panel>
          {Object.keys(compareResults).length > 0 && (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:'10px'}}>
              <div><div style={{fontSize:'10px',color:'var(--t3)',textAlign:'center',marginBottom:'4px'}}>ORIGINAL</div><img src={imgSrc} alt="orig" style={{width:'100%',borderRadius:'3px',border:'1px solid var(--b1)'}}/></div>
              {OPS.map(op=>compareResults[op.id]&&(
                <div key={op.id}><div style={{fontSize:'10px',color:op.color,textAlign:'center',fontWeight:700,marginBottom:'4px'}}>{op.label}</div><img src={compareResults[op.id]} alt={op.id} style={{width:'100%',borderRadius:'3px',border:`1px solid ${op.color}44`}}/></div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab==='concepts' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            {OPS.map(op=>(
              <div key={op.id} style={{padding:'16px',background:'var(--raised)',border:`1px solid ${op.color}33`,borderRadius:'4px'}}>
                <div style={{color:op.color,fontWeight:700,fontSize:'13px',marginBottom:'8px'}}>{op.label}</div>
                <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7}}>{op.desc}</div>
              </div>
            ))}
          </div>
          <Panel title="STRUCTURING ELEMENT" accent="var(--violet)">
            <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.8}}>
              The <strong style={{color:'var(--violet)'}}>structuring element (SE)</strong> is the kernel shape used in morphological operations. Common choices:<br/>
              <strong style={{color:'var(--cyan)'}}>Disk (circle)</strong> — rotation-invariant, smooth results. Used here.<br/>
              <strong style={{color:'var(--green)'}}>Square</strong> — simple, fast, can introduce direction artifacts.<br/>
              <strong style={{color:'var(--gold)'}}>Cross</strong> — connects only 4-neighbors, preserves more detail.<br/>
              Larger SE = stronger effect. For Opening/Closing, SE must be smaller than the feature you want to preserve.
            </div>
          </Panel>
        </div>
      )}

      {!imgData && tab!=='upload' && <InfoBox color="var(--violet)" icon="◉">Go to UPLOAD tab to load an image first.</InfoBox>}
    </div>
  );
}
